import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Schedule } from '@/features/accounts/types';
import { actionable } from '@/features/accounts/schedule/actionable';
import { type ScheduleControls } from '@/features/accounts/schedule/hooks/useScheduleControls';
import DateInterestSummary from '@/features/accounts/schedule/components/DateInterestSummary';
import FormattedAppointment from '@/shared/i18n/components/FormattedAppointment';
import MonitorButton from './MonitorButton';
import ScheduleStatusLabel from '@/features/accounts/schedule/components/ScheduleStatusLabel';
import ScheduleActions from './ScheduleActions';
import ScheduleHeader from './ScheduleHeader';
import ScheduleInfoRow from './ScheduleInfoRow';
import ScheduleSectionBlock from './ScheduleSectionBlock';

interface Props {
  schedule: Schedule;
  actions: ScheduleControls;
}

export default function ScheduleCard({ schedule, actions }: Props) {
  const intl = useIntl();
  const ascLabel = intl.formatMessage({ id: 'schedule.term.asc' });
  const interest = schedule.date_interest;
  const [collapsed, setCollapsed] = useState(!schedule.bookable);

  return (
    <div className="space-y-3 border-t-2 border-slate-200 px-5 py-5 first:border-t-0 first:pt-3">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
        <ScheduleHeader
          schedule={schedule}
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
        />
        {actionable(schedule) && <ScheduleActions schedule={schedule} actions={actions} />}
      </div>

      <MonitorButton schedule={schedule} actions={actions} />

      {!collapsed && (
        <>
          <ScheduleSectionBlock titleId="schedule.list.configuration">
            <ScheduleInfoRow labelId="schedule.list.status">
              <ScheduleStatusLabel status={schedule.status} />
            </ScheduleInfoRow>
            <ScheduleInfoRow labelId="schedule.list.dateRange">
              <DateInterestSummary interest={interest} detail="full" />
            </ScheduleInfoRow>
            {interest.max_gap_between_asc_and_consular != null && (
              <ScheduleInfoRow labelId="schedule.list.maxGap">
                <FormattedMessage
                  id="schedule.list.maxGapDescription"
                  values={{ days: interest.max_gap_between_asc_and_consular }}
                />
              </ScheduleInfoRow>
            )}
            <ScheduleInfoRow labelId="schedule.list.minDaysFromNow">
              <FormattedMessage
                id="schedule.list.minDaysFromNowDescription"
                values={{ days: interest.min_days_from_now }}
              />
            </ScheduleInfoRow>
            {interest.min_same_day_gap != null && (
              <ScheduleInfoRow labelId="schedule.list.minSameDayGap">
                <FormattedMessage
                  id="schedule.list.minSameDayGapDescription"
                  values={{ minutes: interest.min_same_day_gap }}
                />
              </ScheduleInfoRow>
            )}
            {interest.allow_cancellation && (
              <ScheduleInfoRow labelId="schedule.list.allowCancellation">
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  <FormattedMessage id="schedule.list.allowCancellationEnabled" />
                </span>
              </ScheduleInfoRow>
            )}
            {interest.accept_worse_date && (
              <ScheduleInfoRow labelId="schedule.list.acceptWorseDate">
                <FormattedMessage id="schedule.list.acceptWorseDateEnabled" />
              </ScheduleInfoRow>
            )}
          </ScheduleSectionBlock>

          <ScheduleSectionBlock titleId="schedule.list.currentAppointments" tooltipId="schedule.list.currentAppointmentsHelp">
            {schedule.current_asc_at && (
              <ScheduleInfoRow labelId="schedule.list.asc" labelValues={{ asc: ascLabel }}>
                <FormattedAppointment at={schedule.current_asc_at} />
              </ScheduleInfoRow>
            )}
            <ScheduleInfoRow labelId="schedule.list.consular">
              <FormattedAppointment at={schedule.current_consular_at} />
              {schedule.current_consular_facility && ` @ ${schedule.current_consular_facility.name}`}
            </ScheduleInfoRow>
          </ScheduleSectionBlock>

          {schedule.preferred_facilities && schedule.preferred_facilities.length > 0 && (
            <ScheduleSectionBlock titleId="schedule.list.preferredFacilities">
              <div className="flex flex-wrap gap-1.5">
                {schedule.preferred_facilities.map((facility) => (
                  <span
                    key={facility.code}
                    className="rounded-full border border-slate-200 bg-white/80 px-2 py-0.5 text-xs text-slate-600"
                  >
                    {facility.name}
                  </span>
                ))}
              </div>
            </ScheduleSectionBlock>
          )}
        </>
      )}
    </div>
  );
}
